import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { type BillingMethod } from '@/features/billing/profile/BillingMethod'
import { type GetTeamFn } from '@/features/iam'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Job } from '@/features/jobs/Job'
import { type JobStore } from '@/features/jobs/JobStore'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import type { Id } from '@/infra/id'
import { roundToPrecision } from '@/utils/number-util'
import { DEFAULT, match } from '@/utils/pattern-match-util'
import { func, required, validationContext } from 'validx'

/**
 * Whether the dialog charges or refunds the client.
 */
export type ChargeOrRefundMode = 'charge' | 'refund'

/**
 * Represents a billing method option.
 */
export interface BillingMethodOption {
  id: Id
  isDefault: boolean
  displayLabel: string
}

/**
 * View model for the dialog that charges or refunds a job as an admin.
 */
export class ChargeOrRefundDialogViewModel {
  readonly modal = new ModalViewModel()

  /**
   * The reason for the charge or refund.
   */
  @observable
  accessor reason = ''

  /**
   * Billing method selected.
   */
  @observable
  accessor billingMethod: BillingMethodOption | null = null

  /**
   * Amount to charge or refund.
   */
  @observable
  accessor amount = 0

  /**
   * Mode of the dialog.
   */
  @observable
  accessor mode: ChargeOrRefundMode = 'charge'

  /**
   * Validation context.
   */
  readonly validation = validationContext(this, {
    reason: [required('Reason is required.')],
    amount: [
      required('Amount is required.'),
      func((opts) => opts.value > 0, 'Amount must be greater than 0.'),
    ],
  })

  constructor(
    private readonly _job: Job,
    private readonly _canChargeJob: () => boolean,
    private readonly _getTeam: GetTeamFn,
    private readonly _jobStore: JobStore,
    private readonly _getAvailableBillingMethods: () => BillingMethod[],
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Available billing methods.
   */
  @computed
  get availableBillingMethodOptions(): BillingMethodOption[] {
    return this._getAvailableBillingMethods().map(this._toBillingMethodOption)
  }

  /**
   * Disables the submit button.
   */
  @computed
  get disabled() {
    return match(this.mode, {
      charge: () => !this.billingMethod && !this.amount && !this.reason,
      refund: () => !this.amount && !this.reason,
      [DEFAULT]: () => false,
    })
  }

  /**
   * Indicates whether the job can be charged.
   */
  @computed
  get canChargeJob() {
    return this._canChargeJob()
  }

  /**
   * Submits the request for charging or refunding.
   */
  submit = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    const title = this.mode === 'charge' ? 'Charge' : 'Refund'

    const alert = this._newAlert()
      .withState('pending')
      .saying(title, 'Hang tight!')
      .autoDismiss()
      .show()

    try {
      if (this.mode === 'charge') {
        await this._jobStore.chargeJob({
          jobId: this._job.id,
          paymentMethodId: this.billingMethod?.id,
          amount: this.amount,
          reason: this.reason,
        })
      } else {
        await this._jobStore.refundJob({
          jobId: this._job.id,
          amount: this.amount,
          reason: this.reason,
        })
      }

      alert
        .withState('success')
        .saying('Success!', `${title} completed!`)
        .autoDismiss()
        .show()

      this.modal.close()
    } catch (err) {
      alert.dismiss()
      this._createAlertErrorHandler(`${title} failed`)(err)
    }
  })

  /**
   * Shows the dialog with the amount still owed or paid.
   */
  @action.bound
  show() {
    this.reset()
    this.modal.show()
  }

  /**
   * Resets the view model.
   */
  @action.bound
  reset() {
    const amount = roundToPrecision(this._job.total - this._job.paidAmount)

    // A balance still owed is charged; an overpaid one, or a job the user
    // can't charge, is refunded.
    this.mode = this.canChargeJob && amount >= 0 ? 'charge' : 'refund'

    this.setAmount(
      this.mode === 'refund'
        ? Math.abs(this._job.paidAmount)
        : Math.abs(amount),
    )
    this.setReason('')
    this._initializeBillingMethod()

    this.validation.reset()
    this.submit.reset()
  }

  /**
   * Sets the billing method.
   */
  @action.bound
  setBillingMethod(billingMethod: BillingMethodOption | undefined) {
    this.billingMethod = billingMethod ?? null
  }

  /**
   * Sets the amount.
   */
  @action.bound
  setAmount(amount: number) {
    this.amount = amount
  }

  /**
   * Sets the reason.
   */
  @action.bound
  setReason(reason: string) {
    this.reason = reason
  }

  /**
   * Sets the mode.
   */
  @action.bound
  setMode(mode: ChargeOrRefundMode) {
    this.mode = mode
  }

  /**
   * Changes the mode and resets the amount, reason and billing method.
   */
  @action.bound
  changeMode(mode: ChargeOrRefundMode) {
    this.setReason('')
    this.setAmount(0)
    this.setMode(mode)

    if (mode === 'charge') {
      this._initializeBillingMethod()
    }

    this.validation.reset()
  }

  /**
   * Validates the view model.
   */
  @action.bound
  validate() {
    this.validation.reset()

    if (!this.billingMethod && this.mode === 'charge') {
      this.validation.addErrors({
        billingMethod: ['Billing method is required.'],
      })

      return this.validation.validate().isValid
    }

    return this.validation.validate().isValid
  }

  /**
   * Converts a billing method to a billing method option.
   */
  @action.bound
  private _toBillingMethodOption(x: BillingMethod) {
    const isDefault = x.id === x.billingProfile.defaultBillingMethodId

    const defaultLabel = isDefault ? '(default)' : ''
    const teamId = x.billingProfile.teamId
    const team = teamId ? this._getTeam(teamId) : undefined
    const creditTeamName = team?.name ?? 'Unknown'
    const teamName = team?.name ? `${team.name}:` : ''

    const displayLabel =
      x.type.type === 'Credit'
        ? `${creditTeamName} Team Credit`
        : `${teamName} ${x.stripeCard?.brand ?? ''} ending in ${x.stripeCard?.last4 ?? ''} ${defaultLabel}`

    return {
      id: x.id,
      displayLabel,
      isDefault,
    }
  }

  /**
   * Initializes the billing method.
   */
  private _initializeBillingMethod() {
    if (this.availableBillingMethodOptions.length > 0) {
      this.setBillingMethod(
        this.availableBillingMethodOptions.find((x) => x.isDefault) ??
          this.availableBillingMethodOptions[0],
      )
    }
  }
}
